// 로그인 화면 로직. 앱 번들과 분리돼 있다 — 인증 전에는 app.js를 내보내지 않기 때문.

/**
 * 로그인 후 돌아갈 경로를 안전하게 고른다.
 *
 * "/로 시작하는지"만 보면 `//evil.com` 같은 프로토콜 상대 URL이 통과해서
 * 로그인 직후 외부 사이트로 튕긴다(피싱). 같은 출처인지까지 확인한다.
 *
 * @param {string|null} next  ?next= 로 들어온 값
 * @param {string} origin     기준 출처 (기본: 현재 페이지)
 * @returns {string} 항상 같은 출처의 경로
 */
export function safeNext(next, origin = location.origin) {
  if (typeof next !== 'string' || !next) return '/';
  let parsed;
  try {
    parsed = new URL(next, origin);
  } catch {
    return '/';
  }
  if (parsed.origin !== origin) return '/';
  return parsed.pathname + parsed.search + parsed.hash;
}

export function setupLoginForm(doc = document) {
  const f = doc.getElementById('f');
  const err = doc.getElementById('err');
  const go = doc.getElementById('go');
  if (!f) return;

  f.addEventListener('submit', async (e) => {
    e.preventDefault();
    err.classList.remove('on');
    go.disabled = true;
    go.textContent = '확인 중…';
    try {
      const r = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          email: doc.getElementById('email').value,
          password: doc.getElementById('password').value,
        }),
      });
      if (r.ok) {
        location.href = safeNext(new URLSearchParams(location.search).get('next'));
        return;
      }
      const j = await r.json().catch(() => ({}));
      err.textContent = j.error || '로그인에 실패했습니다';
      err.classList.add('on');
    } catch {
      err.textContent = '서버에 연결하지 못했습니다';
      err.classList.add('on');
    }
    go.disabled = false;
    go.textContent = '로그인';
  });
}

/* 패스키 로그인 — 기기가 이 도메인에 등록된 계정을 찾아 서명한다 (discoverable).
   지원 브라우저에서만 버튼이 보인다. 실패해도 비밀번호 로그인이 그대로 남는다. */
const b64uToBuf = (s) => Uint8Array.from(atob(String(s).replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0));
const bufToB64u = (b) => btoa(String.fromCharCode(...new Uint8Array(b))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

export function setupPasskeyLogin(doc = document) {
  const btn = doc.getElementById('pk-go');
  const err = doc.getElementById('err');
  if (!btn || !window.PublicKeyCredential) return;
  btn.hidden = false;
  btn.addEventListener('click', async () => {
    err.classList.remove('on');
    btn.disabled = true;
    try {
      const or = await fetch('/api/auth/passkey/login-options', { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}' });
      const { flowId, options } = await or.json();
      const cred = await navigator.credentials.get({
        publicKey: {
          ...options,
          challenge: b64uToBuf(options.challenge),
          allowCredentials: (options.allowCredentials || []).map((c) => ({ ...c, id: b64uToBuf(c.id) })),
        },
      });
      const r = await fetch('/api/auth/passkey/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          flowId,
          response: {
            id: cred.id, rawId: bufToB64u(cred.rawId), type: cred.type,
            clientExtensionResults: cred.getClientExtensionResults(),
            response: {
              authenticatorData: bufToB64u(cred.response.authenticatorData),
              clientDataJSON: bufToB64u(cred.response.clientDataJSON),
              signature: bufToB64u(cred.response.signature),
              userHandle: cred.response.userHandle ? bufToB64u(cred.response.userHandle) : null,
            },
          },
        }),
      });
      if (r.ok) { location.href = safeNext(new URLSearchParams(location.search).get('next')); return; }
      const j = await r.json().catch(() => ({}));
      err.textContent = j.error || '패스키 로그인에 실패했습니다';
      err.classList.add('on');
    } catch (e) {
      if (e?.name !== 'NotAllowedError') {   // 사용자가 취소한 경우는 조용히
        err.textContent = '패스키 로그인에 실패했습니다 — 비밀번호로 로그인하세요';
        err.classList.add('on');
      }
    }
    btn.disabled = false;
  });
}

if (typeof document !== 'undefined') { setupLoginForm(); setupPasskeyLogin(); }
